import {getPostHogClient} from './posthogClient';
import {
  parsePostHogBooleanFlagFromResponse,
  type PostHogFlagsApiResponse,
} from './posthogFlagParsing';

const orgFlagPromises = new Map<string, Promise<boolean>>();

function normalizePostHogHost(host: string | undefined): string {
  const trimmed = (host ?? 'https://us.i.posthog.com').trim().replace(/^"+|"+$/g, '');
  return trimmed.replace(/\/$/, '');
}

async function fetchOrgBooleanFeatureFlag(
  flagKey: string,
  distinctId: string,
  defaultValue: boolean
): Promise<boolean> {
  if (!import.meta.client) return defaultValue;

  const config = useRuntimeConfig();
  const apiKey = config.public.posthogPublicKey;
  if (!apiKey) return defaultValue;

  const host = normalizePostHogHost(config.public.posthogHost);

  try {
    const response = await fetch(`${host}/flags?v=2`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        api_key: apiKey,
        distinct_id: distinctId,
      }),
    });
    if (!response.ok) return defaultValue;

    const data = (await response.json()) as PostHogFlagsApiResponse;
    return parsePostHogBooleanFlagFromResponse(data, flagKey, defaultValue);
  } catch {
    return defaultValue;
  }
}

/**
 * Evaluate a PostHog boolean feature flag.
 *
 * When `distinctId` is provided (e.g. an org ID), calls the PostHog decide API
 * with that ID so the flag is evaluated in the context of that entity, matching
 * how the backend evaluates flags.
 *
 * When `distinctId` is omitted, evaluates for the current user via the SDK's
 * local cache (sync, no network call).
 *
 * @param flagKey      - Feature flag key
 * @param distinctId   - Optional. Entity ID to evaluate the flag for (e.g. org ID as string).
 *                       Omit to evaluate for the current logged-in user.
 * @param defaultValue - Value to return when PostHog cannot resolve the flag (default: false)
 * @returns Resolved boolean value, or `defaultValue` if PostHog is unavailable
 */
export const isFeatureEnabled = async (
  flagKey: string,
  distinctId?: string | number,
  defaultValue = false
): Promise<boolean> => {
  if (import.meta.server) return defaultValue;

  // User-level evaluation — use the SDK's local cache (sync, toolbar overrides work)
  if (!distinctId) {
    const postHog = getPostHogClient();
    if (!postHog) return defaultValue;
    try {
      return postHog.isFeatureEnabled(flagKey) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  // Org-scoped evaluation — call the decide API with the entity ID as distinct_id.
  // This mirrors how the backend evaluates flags for a specific org.
  const runtimeConfig = useRuntimeConfig();
  const apiKey = runtimeConfig.public.posthogPublicKey as string | undefined;
  const host = normalizePostHogHost(runtimeConfig.public.posthogHost as string | undefined);
  if (!apiKey) return defaultValue;

  try {
    const result = await $fetch<PostHogFlagsApiResponse>(`${host}/decide/?v=3`, {
      method: 'POST',
      body: {api_key: apiKey, distinct_id: String(distinctId)},
    });
    return parsePostHogBooleanFlagFromResponse(result, flagKey, defaultValue);
  } catch {
    return defaultValue;
  }
};

/**
 * Evaluate an org-scoped PostHog boolean flag using the decide `/flags` API with the org id as
 * distinct ID — matching vector-api's `isFeatureEnabled(flag, String(orgId))`.
 *
 * Results are memoized per flag key + org id for the lifetime of the page session.
 */
export const isOrgFeatureEnabled = async (
  flagKey: string,
  orgId: string | number | null | undefined,
  defaultValue = false
): Promise<boolean> => {
  if (orgId == null) return defaultValue;

  const cacheKey = `${flagKey}:${orgId}`;
  let promise = orgFlagPromises.get(cacheKey);
  if (!promise) {
    promise = fetchOrgBooleanFeatureFlag(flagKey, String(orgId), defaultValue).catch(() => {
      orgFlagPromises.delete(cacheKey);
      return defaultValue;
    });
    orgFlagPromises.set(cacheKey, promise);
  }
  return promise;
};
